/**
 * usePortals — Portal API hook
 *
 * Provides CRUD operations for portal management.
 * Extends useApi with portal-specific endpoints.
 */

import { useCallback } from "react";
import useApi from "./useApi";
import { endpoints } from "config/api";
import logger from "services/logger";

export default function usePortals() {
  const { request, loading, error, clearError } = useApi();

  // ==================== User-facing ====================

  /** Get portals accessible by the current user */
  const getMyPortals = useCallback(
    async (lang = "en") => {
      const data = await request(`${endpoints.portals.me}?lang=${lang}`);
      return data.portals || [];
    },
    [request]
  );

  /** Get portal by slug */
  const getPortalBySlug = useCallback(
    async (slug, lang = "en") => {
      const data = await request(`${endpoints.portals.bySlug(slug)}?lang=${lang}`);
      return data.portal || null;
    },
    [request]
  );

  // ==================== Admin CRUD ====================

  /** List all portals (admin) */
  const listPortals = useCallback(
    async (activeOnly = false) => {
      const url = activeOnly ? `${endpoints.portals.base}?active=true` : endpoints.portals.base;
      const data = await request(url);
      return data.portals || [];
    },
    [request]
  );

  /** Get a single portal by ID */
  const getPortal = useCallback(
    async (id) => {
      const data = await request(endpoints.portals.byId(id));
      return data.portal || null;
    },
    [request]
  );

  /** Create a portal */
  const createPortal = useCallback(
    async (portalData) => {
      const data = await request(endpoints.portals.base, {
        method: "POST",
        body: JSON.stringify(portalData),
      });
      logger.info("[usePortals]", `Created portal: ${data.portal?.name}`);
      return data.portal;
    },
    [request]
  );

  /** Update a portal */
  const updatePortal = useCallback(
    async (id, portalData) => {
      const data = await request(endpoints.portals.byId(id), {
        method: "PUT",
        body: JSON.stringify(portalData),
      });
      return data.portal;
    },
    [request]
  );

  /** Delete a portal */
  const deletePortal = useCallback(
    async (id) => {
      await request(endpoints.portals.byId(id), { method: "DELETE" });
      return true;
    },
    [request]
  );

  // ==================== Pages ====================

  /** Upsert a page in a portal */
  const upsertPage = useCallback(
    async (portalId, pageData) => {
      const data = await request(endpoints.portals.pages(portalId), {
        method: "PUT",
        body: JSON.stringify(pageData),
      });
      return data.page;
    },
    [request]
  );

  /** Delete a page from a portal */
  const deletePage = useCallback(
    async (portalId, pageId) => {
      await request(endpoints.portals.deletePage(portalId, pageId), { method: "DELETE" });
      return true;
    },
    [request]
  );

  // ==================== Translations ====================

  /** Get all translations for a portal */
  const getTranslations = useCallback(
    async (portalId) => {
      const data = await request(endpoints.portals.translations(portalId));
      return data.translations || {};
    },
    [request]
  );

  /** Get translations for a specific language */
  const getTranslationsByLang = useCallback(
    async (portalId, lang) => {
      const data = await request(endpoints.portals.translationsByLang(portalId, lang));
      return data.messages || {};
    },
    [request]
  );

  /** Set translations for a language */
  const setTranslations = useCallback(
    async (portalId, lang, messages) => {
      await request(endpoints.portals.translationsByLang(portalId, lang), {
        method: "PUT",
        body: JSON.stringify({ messages }),
      });
      return true;
    },
    [request]
  );

  return {
    // User
    getMyPortals,
    getPortalBySlug,
    // Admin
    listPortals,
    getPortal,
    createPortal,
    updatePortal,
    deletePortal,
    // Pages
    upsertPage,
    deletePage,
    // Translations
    getTranslations,
    getTranslationsByLang,
    setTranslations,
    // State
    loading,
    error,
    clearError,
  };
}
